import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CASE_ORDER, DEFAULT_CASE_SLUG } from "@/constants/content";
import { ProfileCompletionModal } from "@/components/iv/ProfileCompletionModal";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const sortCases = (list) => [...list].sort((a, b) => CASE_ORDER.indexOf(a.slug) - CASE_ORDER.indexOf(b.slug));

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [quota, setQuota] = useState(null);
  const [priceBooks, setPriceBooks] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [activeSlug, setActiveSlug] = useState(DEFAULT_CASE_SLUG);
  const [reports, setReports] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileWaiters = useRef([]);
  const hasMobile = useRef(false);
  hasMobile.current = !!user?.mobile_e164;

  const refreshQuota = useCallback(async () => {
    try {
      setQuota((await api.get("/profile/quota")).data);
    } catch {
      setQuota(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      setUser((await api.get("/auth/me")).data);
      await refreshQuota();
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, [refreshQuota]);

  useEffect(() => {
    // OAuth return: AuthCallback exchanges session_id first, so skip /auth/me here.
    if (window.location.hash?.includes("session_id=")) return setAuthChecked(true);
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    api.get("/price-books").then((r) => setPriceBooks(r.data)).catch(() => toast.error("Couldn't load prices. Please refresh."));
    api.get("/case-studies").then((r) => {
      const list = sortCases(r.data);
      setCaseStudies(list);
      list.forEach((cs) =>
        api.get(`/case-studies/${cs.slug}`)
          .then((d) => setReports((prev) => ({ ...prev, [cs.slug]: d.data })))
          .catch(() => toast.error(`Couldn't load ${cs.title}. Please refresh.`)),
      );
    }).catch(() => toast.error("Couldn't load case studies. Please refresh."));
  }, []);

  const completeLogin = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setUser(data.user);
    setAuthChecked(true);
    await refreshQuota();
    return data.user;
  }, [refreshQuota]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(null);
    setQuota(null);
  }, []);

  // Mobile number is collected once, right after first sign-in; actions that need it await this promise.
  const requireMobile = useCallback(() => {
    if (hasMobile.current) return Promise.resolve(true);
    setMobileOpen(true);
    return new Promise((resolve) => mobileWaiters.current.push(resolve));
  }, []);
  const settleMobile = useCallback((ok) => {
    setMobileOpen(false);
    mobileWaiters.current.splice(0).forEach((resolve) => resolve(ok));
  }, []);
  const onMobileSaved = useCallback((mobile) => {
    hasMobile.current = true;
    setUser((u) => (u ? { ...u, mobile_e164: mobile } : u));
    toast.success("Profile completed");
    settleMobile(true);
  }, [settleMobile]);
  useEffect(() => { if (user && !user.mobile_e164) setMobileOpen(true); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The UI shows USD only; INR books stay server-side for Razorpay charges.
  const priceFor = useCallback((productKey) => priceBooks.find((p) => p.product_key === productKey && p.region === "INTL"), [priceBooks]);

  const value = useMemo(() => ({
    user, authChecked, quota, refreshQuota, completeLogin, logout, priceFor, priceBooks,
    caseStudies, activeSlug, setActiveSlug, activeReport: reports[activeSlug], reports, requireMobile,
  }), [user, authChecked, quota, refreshQuota, completeLogin, logout, priceFor, priceBooks, caseStudies, activeSlug, reports, requireMobile]);

  return (
    <AppCtx.Provider value={value}>
      {children}
      <ProfileCompletionModal open={mobileOpen && !!user} onSaved={onMobileSaved} onDismiss={() => settleMobile(false)} />
    </AppCtx.Provider>
  );
}
